import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { number, object } from "yup";
import { Pageable } from "../../../services/pageable";
import { AllowedBlockchains } from "../../chain/models/internal";
import { countries, processingStatus } from "../../common/models";
import { validationFields } from "../../common/models/fields";
import { StatusEnum } from "../../users/models/internal";

export namespace ParticipationsDTO {
  export namespace Requests {
    export namespace SetSingle {
      export interface T {
        sourceWallet?: {
          address?: string;
          chain?: AllowedBlockchains;
        } | null;
        destinationWallet?: {
          address?: string;
          chain?: AllowedBlockchains;
        } | null;
        stakingWallet?: {
          address?: string;
          chain?: AllowedBlockchains;
        } | null;
        pledge?: {
          amount?: number;
          currency?: string;
        } | null;
      }

      export const useFields = () => {
        const { t } = useTranslation("fields");

        const sourceAddress = validationFields.address.useSchema();
        const sourceChain = validationFields.blockchain.useSchema();
        const destinationAddress = validationFields.address.useSchema();
        const destinationChain = validationFields.blockchain.useSchema();
        const pledge = validationFields.pledge.useSchema();

        return useMemo(
          () =>
            object({
              sourceWallet: object()
                .shape({
                  address: sourceAddress.required(
                    t("required-field", {
                      ns: "errors",
                      field: t("wallet-address"),
                    })
                  ),
                  chain: sourceChain.required(
                    t("required-field", {
                      ns: "errors",
                      field: t("chain"),
                    })
                  ),
                })
                .notRequired()
                .default(undefined),
              destinationWallet: object()
                .shape({
                  address: destinationAddress.required(
                    t("required-field", {
                      ns: "errors",
                      field: t("wallet-address"),
                    })
                  ),
                  chain: destinationChain.required(
                    t("required-field", {
                      ns: "errors",
                      field: t("chain"),
                    })
                  ),
                })
                .notRequired()
                .default(undefined),
              pledge,
            }),
          [
            t,
            sourceAddress,
            sourceChain,
            destinationAddress,
            destinationChain,
            pledge,
          ]
        );
      };
    }
  }

  export namespace Responses {
    export namespace GetParticipationSummary {
      export interface T {
        totalFromBeginningOfTime: number;
        maxPerWeek: number;
        totalPerWeek: { date: string; count: number }[];
      }

      export const useFields = () => {
        const { t } = useTranslation("fields");
        return useMemo(
          () =>
            object({
              totalFromBeginningOfTime: number().label(
                t("total-from-beginning-of-time")
              ),
              maxPerWeek: number().label(t("max-per-week")),
              totalPerWeek: object().label(t("total-per-week")),
            }).required(),
          [t]
        );
      };

      export const defaults: T = {
        totalFromBeginningOfTime: 0,
        maxPerWeek: 0,
        totalPerWeek: [],
      };
    }
    export namespace GetSingle {
      export interface T {
        configurationId: string;
        createdDate: Date;
        deleted: boolean;
        destinationWallet?: {
          address: string;
          chain: AllowedBlockchains;
        };
        editable: boolean;
        id: string;
        fraudCheckId: string;
        credentialsId: string;
        kycStatus: StatusEnum;
        pledge?: {
          amount: number;
          currency: string;
        };
        policies: {
          sekuritanceStatements: boolean;
          finalDeclaration: boolean;
          declarationStatements: boolean;
          whitepaperRead: boolean;
        };
        processingStatus: typeof processingStatus.T;
        referrer: string;
        regulations?: {
          pep: boolean;
          resideSanctionedCountry: boolean;
          usPerson: boolean;
        };
        sourceWallet?: {
          address: string;
          chain: AllowedBlockchains;
        };
        stakingWallet?: {
          address: string;
          chain: AllowedBlockchains;
        };
        user: {
          address: {
            addressLine1: string;
            addressLine2: string;
            city: string;
            country: typeof countries.T;
            state?: string;
            zip: string;
          };
          applicationPin: string;
          countryOfResidence: typeof countries.T;
          dob: Date;
          emailAddress: string;
          firstName: string;
          lastName: string;
          locale: string;
          middleName?: string;
          nationality: typeof countries.T;
          physicallyLocatedIn: typeof countries.T;
        };
      }
    }
    export namespace GetAll {
      export interface T {
        content: GetSingle.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };
    }
  }
}
